import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiRequest } from '@/lib/supabase'
import { useWizardState } from '@/components/bio/wizard/useWizardState'
import { WizardStepIndicator } from '@/components/bio/wizard/WizardStepIndicator'
import { WizardStepType } from '@/components/bio/wizard/WizardStepType'
import { WizardStepProfile } from '@/components/bio/wizard/WizardStepProfile'
import { WizardStepAppearance } from '@/components/bio/wizard/WizardStepAppearance'
import { WizardStepLinks } from '@/components/bio/wizard/WizardStepLinks'
import { WizardStepReview } from '@/components/bio/wizard/WizardStepReview'
import { BioPhonePreview } from '@/components/bio/BioPhonePreview'

const TOTAL_STEPS = 5

export function BioPageWizard() {
  const [currentStep, setCurrentStep] = useState(1)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const wizard = useWizardState()

  const createMutation = useMutation({
    mutationFn: async () => {
      const payload = wizard.toApiPayload()
      const res = await apiRequest<{ success: boolean; data: { id: string } }>('/api/bio-pages', 'POST', payload)
      const pageId = res.data.id

      // Create starter links sequentially
      for (let i = 0; i < wizard.state.links.length; i++) {
        const link = wizard.state.links[i]
        await apiRequest(`/api/bio-pages/${pageId}/links`, 'POST', {
          title: link.title,
          url: link.url,
          order_index: i,
          is_active: true,
          type: 'link',
        })
      }

      return pageId
    },
    onSuccess: (pageId) => {
      queryClient.invalidateQueries({ queryKey: ['bio-pages'] })
      navigate(`/bio-pages/${pageId}`)
    },
    onError: (err: Error) => {
      setSubmitError(err.message || 'Failed to create bio page')
    },
  })

  function canAdvance(): boolean {
    switch (currentStep) {
      case 1: return true // type selection always has a default
      case 2: return wizard.state.display_name.trim().length > 0 && wizard.state.handle.length >= 3 && wizard.slugAvailable !== false
      case 3: return true // theme always selected
      case 4: return true // links are optional
      default: return true
    }
  }

  function handleNext() {
    if (currentStep < TOTAL_STEPS && canAdvance()) {
      setCurrentStep(prev => prev + 1)
    }
  }

  function handleBack() {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1)
    }
  }

  function handleSubmit() {
    setSubmitError(null)
    createMutation.mutate()
  }

  const showPreview = currentStep >= 2

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link
          to="/bio-pages"
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        >
          <span className="material-symbols-outlined text-[22px]">arrow_back</span>
        </Link>
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">Create Bio Page</h1>
      </div>

      {/* Step Indicator */}
      <div className="mb-10">
        <WizardStepIndicator currentStep={currentStep} totalSteps={TOTAL_STEPS} />
      </div>

      {/* Content: two-column on xl */}
      <div className="flex gap-12">
        {/* Left: Step Content */}
        <div className="flex-1 min-w-0">
          {currentStep === 1 && (
            <WizardStepType
              pageType={wizard.state.pageType}
              onChange={type => wizard.update({ pageType: type })}
            />
          )}
          {currentStep === 2 && (
            <WizardStepProfile
              state={wizard.state}
              onChange={wizard.update}
              slugAvailable={wizard.slugAvailable}
              checkingSlug={wizard.checkingSlug}
            />
          )}
          {currentStep === 3 && (
            <WizardStepAppearance
              state={wizard.state}
              onChange={wizard.update}
            />
          )}
          {currentStep === 4 && (
            <WizardStepLinks
              links={wizard.state.links}
              onAdd={wizard.addLink}
              onRemove={wizard.removeLink}
            />
          )}
          {currentStep === 5 && (
            <WizardStepReview
              state={wizard.state}
              onChange={wizard.update}
              onGoToStep={setCurrentStep}
              onSubmit={handleSubmit}
              isSubmitting={createMutation.isPending}
              error={submitError}
            />
          )}

          {/* Navigation Buttons */}
          {currentStep < 5 && (
            <div className="flex items-center justify-between mt-10 pt-6 border-t border-gray-200 dark:border-white/10">
              {currentStep > 1 ? (
                <button
                  onClick={handleBack}
                  className="px-5 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5 rounded-lg transition-colors"
                >
                  Back
                </button>
              ) : (
                <div />
              )}
              <div className="flex items-center gap-3">
                {currentStep === 4 && (
                  <button
                    onClick={() => setCurrentStep(5)}
                    className="px-5 py-2.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5 rounded-lg transition-colors"
                  >
                    Skip
                  </button>
                )}
                <button
                  onClick={handleNext}
                  disabled={!canAdvance()}
                  className="px-6 py-2.5 bg-[#2B6CEE] text-white text-sm font-semibold rounded-lg hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right: Phone Preview (xl+ only) */}
        {showPreview && (
          <div className="hidden xl:block sticky top-8 self-start">
            <p className="text-xs font-medium text-gray-400 dark:text-gray-500 text-center mb-3">Preview</p>
            <BioPhonePreview
              page={wizard.toPreviewPageData()}
              links={wizard.toPreviewLinks()}
            />
          </div>
        )}
      </div>
    </div>
  )
}
